/**
 * Shipments Page - MPAD Phase 2 Implementation  
 * Shipments Implementation with Pickup-Delivery Workflows
 */

import React from 'react';
import { Container, Box, Typography, Paper, Alert } from '@mui/material';
import { LocalShipping, Warning } from '@mui/icons-material';
import ShipmentWizard from '../components/Shipments/ShipmentWizard';
import { useProject } from '../contexts/ProjectContext';
import { useScenario } from '../contexts/ScenarioContext';
import { useDataset } from '../contexts/DatasetContext';
import { useCreateShipment } from '../hooks/useShipments';

const ShipmentsPage: React.FC = () => {
  const { currentProject } = useProject();
  const { currentScenario } = useScenario();
  const { currentDataset } = useDataset();
  const createShipment = useCreateShipment();

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: 'bold',
            color: 'primary.main'
          }}
        >
          <LocalShipping />
          Shipments Management
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          MPAD Phase 2: Create and manage pickup-delivery shipments with advanced workflow support.
          Configure time windows, product assignments, and delivery constraints.
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        {!currentProject ? (
          <Typography variant="body1" color="text.secondary">
            Please select a project to manage shipments.
          </Typography>
        ) : !currentScenario ? (
          <Alert 
            severity="warning" 
            icon={<Warning />}
            sx={{ mb: 3 }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              Scenario Required for Shipment Creation
            </Typography>
            <Typography variant="body2">
              Shipments must belong to a scenario for proper data organization and planning context. 
              Please select or create a scenario first to proceed with shipment creation.
            </Typography>
          </Alert>
        ) : (
          <ShipmentWizard 
            projectId={currentProject.id}
            onSave={async (shipment) => {
              // MPAD Enhancement: Include scenario/dataset context in shipment creation
              const enhancedShipment = {
                ...shipment,
                // Required scenario context (validated by Edge Function)
                scenario_id: currentScenario.id,
                // Optional dataset context (if available)
                dataset_id: currentDataset?.id || null,
                dataset_name: currentDataset?.name || 'Default Dataset',
                dataset_version: currentDataset?.version || 1,
                // Data lineage (for new shipments, not derived)
                is_derived: false,
                derivation_metadata: {}
              };
              
              console.log('🚀 Creating shipment with enhanced context:', {
                scenario: currentScenario.name,
                dataset: currentDataset?.name || 'None selected',
                shipment: enhancedShipment
              });
              
              await createShipment.mutateAsync({ 
                shipment: enhancedShipment, 
                projectId: currentProject.id 
              });
            }}
            onCancel={() => {
              console.log('Shipment creation cancelled');
            }}
            mode="create"
          />
        )}
      </Paper>
    </Container>
  );
};

export { ShipmentsPage };